import { FC, ReactNode } from "react";
import { Helmet } from "react-helmet";
import { Navigate, useLocation } from "react-router-dom";
import { useAccountContext } from "../../strict/app/AccountResolver";

import Permissions from "../../constants/permissions";
import useAuth from "../../context/UserContext";
import { useNavigator } from "../../lib/Hooks";
import { hasPermission } from "../Permissions";

export const ToDashboard = () => {
  const { urlName } = useAccountContext();
  const { user } = useAuth();

  const accountUrlName = urlName ?? user?.default_url_name ?? null;
  return (
    <Navigate
      to={{
        pathname: accountUrlName !== null ? `/app/${accountUrlName}/dashboard` : "/",
      }}
    />
  );
};

export const ToEditorVersion = ({ versionId }: { versionId: string }) => {
  const nav = useNavigator();
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: nav.route(["scripts", "editor", versionId]),
        search: `referrer=${location.pathname}`,
      }}
    />
  );
};

interface DocumentHeadWrapperProps {
  /**
   * The title of the webpage
   * */
  title: PageTitle;
  /**
   * React nodes to be rendered as children of this component
   * */
  children: ReactNode;
}

const isAdminPageTitle = (title: PageTitle): title is AdminPageTitle => {
  const adminTitles: AdminPageTitle[] = ["Users", "User Details", "Accounts", "Admin Accounts"];
  return (adminTitles as PageTitle[]).includes(title);
};

const getName = (accountName: string | null, title: PageTitle) => {
  if (accountName) return `| ${accountName} `;

  if (isAdminPageTitle(title)) return " | Admin ";

  return "";
};

export const DocumentHeadWrapper: FC<DocumentHeadWrapperProps> = ({ title, children }) => {
  const { accountName } = useAccountContext();

  return (
    <>
      <Helmet>
        <title>
          {title} {getName(accountName, title)}| Switchboard
        </title>
      </Helmet>
      {children}
    </>
  );
};

interface RequireDevModeProps {
  /**
   * React nodes to be rendered as children of this component
   */
  children: ReactNode;
}

export const RequireDevMode: React.FC<RequireDevModeProps> = ({ children }) => {
  const { user } = useAuth();

  return hasPermission(Permissions.TestScreens, user) ? <>{children}</> : <Navigate to="/" />;
};
